import { useState, useEffect, useMemo } from "react";
import { formatDateTR } from "@/utils/dateUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, TrendingUp, TrendingDown, CreditCard, Package, DollarSign, CheckCircle, Clock, List, AlertCircle, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";
import { getMonthNames } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function TrainerCommissionReportModal({ isOpen, onClose, trainer, businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "reports", "packages"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (isOpen && trainer) {
      fetchReport();
    }
  }, [isOpen, trainer, selectedMonth, selectedYear]);

  const fetchReport = async () => {
    if (!trainer?.trainer_id) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API}/trainers/${trainer.trainer_id}/my-commissions?month=${selectedMonth}&year=${selectedYear}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error(t("reports:toast_raporYuklenemedi"));
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error(t('common:errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return formatDateTR(dateStr);
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    years.push(y);
  }

  // ========= RECEIVED COMMISSIONS (from business) =========
  // Filter out external_member (those are "given" commissions)
  const receivedDetailsWithStatus = useMemo(() => {
    if (!reportData?.details) return [];

    const receivedOnly = reportData.details.filter(d => d.type !== "external_member" && !d.is_expense);
    const paidAmount = reportData.paid_amount || 0;

    // FIFO: sort ascending by date/time
    const sorted = [...receivedOnly].sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return (a.time || '').localeCompare(b.time || '');
    });

    let remainingPaid = paidAmount;
    return sorted.map(item => {
      const commission = item.commission || 0;
      let paymentStatus = 'pending';
      let paidAmountForThis = 0;

      if (remainingPaid >= commission) {
        paymentStatus = 'paid';
        paidAmountForThis = commission;
        remainingPaid -= commission;
      } else if (remainingPaid > 0) {
        paymentStatus = 'partial';
        paidAmountForThis = remainingPaid;
        remainingPaid = 0;
      }

      return {
        ...item,
        paymentStatus,
        paidAmountForThis,
        remainingAmount: commission - paidAmountForThis
      };
    });
  }, [reportData]);

  // ========= GIVEN COMMISSIONS (to business, for external members) =========
  const givenDetailsWithStatus = useMemo(() => {
    if (!reportData?.details) return [];

    const givenOnly = reportData.details.filter(d => d.type === "external_member" || d.is_expense);
    const paidExternal = reportData.external_commission_paid || 0;

    const sorted = [...givenOnly].sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return (a.time || '').localeCompare(b.time || '');
    });

    let remainingPaid = paidExternal;
    return sorted.map(item => {
      const commission = item.commission || 0;
      let paymentStatus = 'pending';
      let paidAmountForThis = 0;

      if (remainingPaid >= commission) {
        paymentStatus = 'paid';
        paidAmountForThis = commission;
        remainingPaid -= commission;
      } else if (remainingPaid > 0) {
        paymentStatus = 'partial';
        paidAmountForThis = remainingPaid;
        remainingPaid = 0;
      }

      return {
        ...item,
        paymentStatus,
        paidAmountForThis,
        remainingAmount: commission - paidAmountForThis
      };
    });
  }, [reportData]);

  // Split received by type for sub-tabs
  const cardDetails = receivedDetailsWithStatus.filter(d => d.type === "card" || d.type === "card_lesson");
  const packageDetails = receivedDetailsWithStatus.filter(d => d.type === "package" || d.type === "package_lesson" || d.type === "package_sale");

  const renderPaymentStatusBadge = (item) => {
    if (item.paymentStatus === 'paid') {
      return (
        <Badge className="bg-green-100 text-green-700" data-testid="status-badge-paid">
          <CheckCircle className="w-3 h-3 mr-1" />
          {t("common:paidStatus")}
        </Badge>
      );
    } else if (item.paymentStatus === 'partial') {
      return (
        <Badge className="bg-orange-100 text-orange-700" data-testid="status-badge-partial">
          <AlertCircle className="w-3 h-3 mr-1" />
          {t("common:partialPayment")}
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-yellow-100 text-yellow-700" data-testid="status-badge-pending">
          <Clock className="w-3 h-3 mr-1" />
          {t("common:pending")}
        </Badge>
      );
    }
  };

  if (!trainer) return null;

  // Totals for received
  const receivedTotal = reportData?.total_commission || 0;
  const receivedPaid = reportData?.paid_amount || 0;
  const receivedPending = reportData?.pending_amount || 0;
  const receivedSessions = receivedDetailsWithStatus.length;

  // Totals for given
  const givenTotal = reportData?.external_commission || 0;
  const givenPaid = reportData?.external_commission_paid || 0;
  const givenPending = reportData?.external_commission_pending || 0;
  const givenSessions = givenDetailsWithStatus.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="trainer-commission-report-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            {t("reports:commissionReportTitle")} - {trainer.name} {trainer.surname}
          </DialogTitle>
        </DialogHeader>

        {/* Month/Year Selector */}
        <div className="flex gap-3 mb-4">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-32 rounded-xl" data-testid="commission-report-month-select">
              <SelectValue>{getMonthNames()[selectedMonth - 1]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {getMonthNames().map((month, idx) => (
                <SelectItem key={idx + 1} value={String(idx + 1)}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] rounded-xl" data-testid="commission-report-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-navy" />
          </div>
        ) : reportData ? (
          <Tabs defaultValue="received" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 rounded-xl" data-testid="main-commission-tabs">
              <TabsTrigger value="received" className="rounded-xl flex items-center gap-2" data-testid="tab-received-commissions">
                <ArrowDownCircle className="w-4 h-4 text-green-600" />
                {t("reports:receivedCommissionsTab", "İşletmeden Alınan Primler")}
                {givenSessions > 0 || receivedSessions > 0 ? (
                  <Badge variant="secondary" className="ml-1">{formatCurrency(receivedTotal)}</Badge>
                ) : null}
              </TabsTrigger>
              <TabsTrigger value="given" className="rounded-xl flex items-center gap-2" data-testid="tab-given-commissions">
                <ArrowUpCircle className="w-4 h-4 text-red-600" />
                {t("reports:givenCommissionsTab", "İşletmeye Verilen Primler")}
                {givenTotal > 0 ? (
                  <Badge variant="secondary" className="ml-1">{formatCurrency(givenTotal)}</Badge>
                ) : null}
              </TabsTrigger>
            </TabsList>

            {/* ============ RECEIVED COMMISSIONS TAB ============ */}
            <TabsContent value="received" className="space-y-4 mt-4">
              {/* Summary Cards - Received */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="received-summary-cards">
                <Card className="border-0 shadow-sm bg-blue-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-blue-600 mb-1">{t("members:totalSessions")}</div>
                    <div className="text-2xl font-bold text-blue-700" data-testid="received-total-sessions">{receivedSessions}</div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-green-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-green-600 mb-1">{t("reports:totalCommission2")}</div>
                    <div className="text-2xl font-bold text-green-700" data-testid="received-total-commission">{formatCurrency(receivedTotal)}</div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-purple-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-purple-600 mb-1">{t("common:paidAmount")}</div>
                    <div className="text-2xl font-bold text-purple-700" data-testid="received-paid-amount">{formatCurrency(receivedPaid)}</div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-orange-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-orange-600 mb-1">{t("common:pending")}</div>
                    <div className="text-2xl font-bold text-orange-700" data-testid="received-pending-amount">{formatCurrency(receivedPending)}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Sub-tabs: All / Card / Package */}
              <Tabs defaultValue="all">
                <TabsList className="grid w-full grid-cols-3 rounded-xl">
                  <TabsTrigger value="all" className="rounded-xl flex items-center gap-2" data-testid="received-subtab-all">
                    <List className="w-4 h-4" />
                    {t("common:allCount")} ({receivedDetailsWithStatus.length})
                  </TabsTrigger>
                  <TabsTrigger value="card" className="rounded-xl flex items-center gap-2" data-testid="received-subtab-card">
                    <CreditCard className="w-4 h-4" />
                    {t("common:cardLabel")} ({cardDetails.length})
                  </TabsTrigger>
                  <TabsTrigger value="package" className="rounded-xl flex items-center gap-2" data-testid="received-subtab-package">
                    <Package className="w-4 h-4" />
                    {t("packages:packageLabel")} ({packageDetails.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="mt-4">
                  {receivedDetailsWithStatus.length > 0 ? (
                    <div className="rounded-xl border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>{t('common:date')}</TableHead>
                            <TableHead>{t("common:member")}</TableHead>
                            <TableHead>{t("common:typeLabel")}</TableHead>
                            <TableHead>{t('reservations:lessonType')}</TableHead>
                            <TableHead>{t("common:rate")}</TableHead>
                            <TableHead className="text-right">{t("reports:totalCommission2")}</TableHead>
                            <TableHead>{t('common:status')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {receivedDetailsWithStatus.map((item, index) => (
                            <TableRow key={index} data-testid={`received-row-${index}`}>
                              <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                              <TableCell>{item.member_name || "-"}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {item.type === "card" || item.type === "card_lesson" ? t("common:cardLabel") : t('packages:package')}
                                </Badge>
                              </TableCell>
                              <TableCell>{getLocalizedTrainingType ? getLocalizedTrainingType(item.training_type) : (item.training_type || item.package_name || "-")}</TableCell>
                              <TableCell>
                                {item.rate_type === "percentage" ? `%${item.rate}` : formatCurrency(item.rate)}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-green-600">
                                {formatCurrency(item.commission)}
                              </TableCell>
                              <TableCell>{renderPaymentStatusBadge(item)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-grey" data-testid="received-empty">
                      {t("reports:noCommissionThisPeriod")}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="card" className="mt-4">
                  {cardDetails.length > 0 ? (
                    <div className="rounded-xl border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>{t('common:date')}</TableHead>
                            <TableHead>{t("common:member")}</TableHead>
                            <TableHead>{t('reservations:lessonType')}</TableHead>
                            <TableHead>{t("common:rate")}</TableHead>
                            <TableHead className="text-right">{t("reports:totalCommission2")}</TableHead>
                            <TableHead>{t('common:status')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cardDetails.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                              <TableCell>{item.member_name || "-"}</TableCell>
                              <TableCell>{getLocalizedTrainingType ? getLocalizedTrainingType(item.training_type) : (item.training_type || "-")}</TableCell>
                              <TableCell>
                                {item.rate_type === "percentage" ? `%${item.rate}` : formatCurrency(item.rate)}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-green-600">
                                {formatCurrency(item.commission)}
                              </TableCell>
                              <TableCell>{renderPaymentStatusBadge(item)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-grey">
                      {t("reports:noCardCommThisPeriod")}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="package" className="mt-4">
                  {packageDetails.length > 0 ? (
                    <div className="rounded-xl border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>{t('common:date')}</TableHead>
                            <TableHead>{t("common:member")}</TableHead>
                            <TableHead>{t("packages:package")}</TableHead>
                            <TableHead>{t("common:rate")}</TableHead>
                            <TableHead className="text-right">{t("reports:totalCommission2")}</TableHead>
                            <TableHead>{t('common:status')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {packageDetails.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                              <TableCell>{item.member_name || "-"}</TableCell>
                              <TableCell>{item.package_name || item.training_type || "-"}</TableCell>
                              <TableCell>
                                {item.rate_type === "percentage" ? `%${item.rate}` : formatCurrency(item.rate)}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-green-600">
                                {formatCurrency(item.commission)}
                              </TableCell>
                              <TableCell>{renderPaymentStatusBadge(item)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-grey">
                      {t("packages:noPackageCommThisPeriod")}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* ============ GIVEN COMMISSIONS TAB ============ */}
            <TabsContent value="given" className="space-y-4 mt-4">
              {/* Info banner */}
              <div className="rounded-xl bg-red-50 border border-red-100 p-3 text-sm text-red-700 flex items-start gap-2">
                <TrendingDown className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  {t(
                    "reports:givenCommissionsInfo",
                    "Bu sekme, antrenörün dış üyeleri için işletmeye ödemesi gereken primleri listeler. Antrenör bu tutarları işletmeye öder."
                  )}
                </div>
              </div>

              {/* Summary Cards - Given */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="given-summary-cards">
                <Card className="border-0 shadow-sm bg-blue-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-blue-600 mb-1">{t("members:totalSessions")}</div>
                    <div className="text-2xl font-bold text-blue-700" data-testid="given-total-sessions">{givenSessions}</div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-red-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-red-600 mb-1">{t("reports:givenCommissionTotal", "Verilen Prim (Toplam)")}</div>
                    <div className="text-2xl font-bold text-red-700" data-testid="given-total-commission">{formatCurrency(givenTotal)}</div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-green-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-green-600 mb-1">{t("reports:givenCommissionPaid", "Ödendi")}</div>
                    <div className="text-2xl font-bold text-green-700" data-testid="given-paid-amount">{formatCurrency(givenPaid)}</div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-orange-50">
                  <CardContent className="p-4">
                    <div className="text-sm text-orange-600 mb-1">{t("common:pending")}</div>
                    <div className="text-2xl font-bold text-orange-700" data-testid="given-pending-amount">{formatCurrency(givenPending)}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Given commissions table */}
              {givenDetailsWithStatus.length > 0 ? (
                <div className="rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>{t('common:date')}</TableHead>
                        <TableHead>{t("common:member")}</TableHead>
                        <TableHead>{t('reservations:lessonType')}</TableHead>
                        <TableHead>{t("common:rate")}</TableHead>
                        <TableHead className="text-right">{t("reports:givenCommissionCol", "Verilen Prim")}</TableHead>
                        <TableHead>{t('common:status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {givenDetailsWithStatus.map((item, index) => (
                        <TableRow key={index} data-testid={`given-row-${index}`}>
                          <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{item.member_name || "-"}</span>
                              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                {t("reservations:externalMember", "Dış Üye")}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>{getLocalizedTrainingType ? getLocalizedTrainingType(item.training_type) : (item.training_type || "-")}</TableCell>
                          <TableCell>
                            {item.rate_type === "percentage" ? `%${item.rate}` : formatCurrency(item.rate)}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            -{formatCurrency(item.commission)}
                          </TableCell>
                          <TableCell>{renderPaymentStatusBadge(item)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-grey" data-testid="given-empty">
                  {t("reports:noGivenCommissionThisPeriod", "Bu dönemde verilen prim bulunmuyor.")}
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="text-center py-8 text-grey">
            {t("reports:reportDataNotFound")}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" className="rounded-xl" onClick={onClose} data-testid="commission-report-close-btn">
            {t("common:close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
